import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { CnpjaMappedCompany } from '@/api/integrations/cnpja';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { LocalAccount } from '@/context/create-account-flow';
import { api } from '@/lib/api';

interface AccountRefreshDialogProps {
  open: boolean;
  onClose: () => void;
  account: LocalAccount;
  onConfirm: (data: Partial<LocalAccount>) => void;
}

const FIELD_LABELS: Record<string, string> = {
  social_name: 'Razão Social',
  fantasy_name: 'Nome Fantasia',
  status: 'Situação Cadastral',
  state_registration: 'Inscrição Estadual',
  situationIE: 'Situação da IE',
  typeIE: 'Tipo da IE',
  accountType: 'Porte',
  simeiSince: 'Optante SIMEI desde',
  natureText: 'Natureza Jurídica',
};

interface DiffRow {
  field: string;
  label: string;
  current: string;
  incoming: string;
}

function computeIncomingValues(
  data: CnpjaMappedCompany,
  normalized: Record<string, unknown>,
): Partial<LocalAccount> {
  const simei = normalized.simei as { optant?: boolean; since?: string } | undefined;
  const size = normalized.size as { acronym?: string } | undefined;
  const nature = normalized.nature as { text?: string } | undefined;

  const registration = data.registrations?.[0];

  let accountType: LocalAccount['accountType'] | undefined;
  if (simei?.optant) {
    accountType = 'MEI';
  } else if (data.company?.simei?.since) {
    accountType = 'MEI';
  } else if (size?.acronym) {
    accountType = size.acronym as LocalAccount['accountType'];
  }

  const situationIE = registration
    ? {
        id: registration.enabled ? '1' : '2',
        status: registration.enabled,
        text: registration.enabled ? 'Habilitada' : 'Não habilitada',
      }
    : undefined;

  return {
    social_name: data.company?.name ?? undefined,
    fantasy_name: data.alias ?? undefined,
    status: registration?.status?.text ?? undefined,
    state_registration: registration?.number ?? undefined,
    situationIE,
    typeIE: registration?.type?.text ?? undefined,
    accountType,
    simeiSince: (simei?.since ?? data.company?.simei?.since) || undefined,
    natureText: nature?.text ?? undefined,
  };
}

function getDisplayValue(
  field: string,
  // biome-ignore lint/suspicious/noExplicitAny: field value varies
  value: any,
): string {
  if (value == null) return '';
  if (field === 'situationIE') {
    return value?.text ?? '';
  }
  return String(value);
}

function computeDiff(account: LocalAccount, incoming: Partial<LocalAccount>): DiffRow[] {
  const rows: DiffRow[] = [];

  for (const field of Object.keys(FIELD_LABELS)) {
    const currentVal = getDisplayValue(field, account[field as keyof LocalAccount]);
    const incomingVal = getDisplayValue(field, incoming[field as keyof LocalAccount]);

    if (incomingVal !== '' && incomingVal !== currentVal) {
      rows.push({
        field,
        label: FIELD_LABELS[field],
        current: currentVal,
        incoming: incomingVal,
      });
    }
  }

  return rows;
}

export function AccountRefreshDialog({
  open,
  onClose,
  account,
  onConfirm,
}: AccountRefreshDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diff, setDiff] = useState<DiffRow[]>([]);
  const [incomingData, setIncomingData] = useState<Partial<LocalAccount> | null>(null);

  useEffect(() => {
    if (!open) {
      setDiff([]);
      setError(null);
      setIncomingData(null);
      return;
    }

    const cnpj = account.document.value.replace(/\D/g, '');
    if (!cnpj) {
      setError('CNPJ não encontrado na conta.');
      return;
    }

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const [registrationRes, normalizedRes] = await Promise.all([
          api.get<CnpjaMappedCompany>(`/integrations/cnpja/${cnpj}/registration`),
          api.get(`/integrations/cnpja/${cnpj}/normalized`),
        ]);

        if (cancelled) return;

        const data = registrationRes.data;
        const normalized =
          (normalizedRes.data?.metadata?.company as Record<string, unknown> | undefined) ?? {};

        const incoming = computeIncomingValues(data, normalized);
        const diffRows = computeDiff(account, incoming);

        setIncomingData(incoming);
        setDiff(diffRows);
      } catch {
        if (!cancelled) {
          setError('Erro ao buscar dados atualizados do CNPJ.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [open, account]);

  const handleConfirm = () => {
    if (!incomingData) return;

    const changedFields: Partial<LocalAccount> = {};
    for (const row of diff) {
      const key = row.field as keyof LocalAccount;
      // biome-ignore lint/suspicious/noExplicitAny: dynamic field assignment
      (changedFields as any)[key] = incomingData[key];
    }

    onConfirm(changedFields);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Atualizar Dados do CNPJ</DialogTitle>
          <DialogDescription>
            Comparação entre os dados atuais e os dados mais recentes da Receita Federal.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Buscando dados atualizados...
            </span>
          </div>
        )}

        {error && <div className="py-6 text-center text-sm text-destructive">{error}</div>}

        {!loading && !error && diff.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Nenhuma alteração encontrada.
          </div>
        )}

        {!loading && !error && diff.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium">Campo</th>
                  <th className="text-left px-3 py-2 font-medium">Valor Atual</th>
                  <th className="text-left px-3 py-2 font-medium">Novo Valor</th>
                </tr>
              </thead>
              <tbody>
                {diff.map((row) => (
                  <tr key={row.field} className="border-b last:border-b-0">
                    <td className="px-3 py-2 text-muted-foreground">{row.label}</td>
                    <td className="px-3 py-2 bg-destructive/5">
                      {row.current || <span className="text-muted-foreground italic">Vazio</span>}
                    </td>
                    <td className="px-3 py-2 font-medium bg-green-50 dark:bg-green-950/20">
                      {row.incoming}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={loading || diff.length === 0}>
            Atualizar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
